import { useMemo, useState } from "react";
import { Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsIconButton, Typography, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { fetchAllAuditLogEntries, useGetAuditLogFeed } from "src/api/client-api/auditLog/useGetAuditLogFeed";
import type { AuditLogEntry } from "src/api/client-api/auditLog/auditLogSchemas";
import { URLHandler } from "src/services/URLHandler";
import { constrainsNothing, type FacetSelection } from "src/components/FacetFilters";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { useSnackbar } from "src/utils/useSnackbar";
import { AuditLogCardList } from "./AuditLogCardList";
import { AuditLogDrawer } from "./AuditLogDrawer";
import { AuditLogFacetRail } from "./AuditLogFacetRail";
import { AuditLogFilterSheet } from "./AuditLogFilterSheet";
import { AuditLogGrid } from "./AuditLogGrid";
import { AuditLogQueryBar } from "./AuditLogQueryBar";
import { AuditLogSummary } from "./AuditLogSummary";
import { AuditLogEmptyState, AuditLogErrorState, AuditLogLoadingState } from "./AuditLogStates";
import { AuditLogTableMenu } from "./AuditLogTableMenu";
import { auditLogCsvFilename, auditLogToCsv } from "./auditLogCsv";
import {
    auditLogDisplayTotal,
    emptyAuditLogCustomWindow,
    type AuditLogCustomWindow,
    type AuditLogPeriod,
} from "./auditLogPresentation";
import {
    buildAuditLogQuery,
    parseAuditLogQuery,
    tokenizeAuditLogQuery,
    type AuditLogQueryFilters,
} from "./auditLogQuery";
import { useAuditLogFacetSections, type AuditLogFacetSection } from "./useAuditLogFacetSections";
import { useAuditLogTablePreferences } from "./useAuditLogTablePreferences";
import { useAuditLogWindow } from "./useAuditLogWindow";

const WIDER_PERIOD: Record<AuditLogPeriod, AuditLogPeriod> = {
    day: "week",
    week: "month",
    month: "all",
    all: "all",
    custom: "week",
};

export const AuditLogPage = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isMobile } = useResponsiveBreakpoints();

    const [appliedQuery, setAppliedQuery] = useState("");
    const [period, setPeriod] = useState<AuditLogPeriod>("day");
    const [customWindowDraft, setCustomWindowDraft] = useState<AuditLogCustomWindow>(emptyAuditLogCustomWindow);
    const [customWindow, setCustomWindow] = useState<AuditLogCustomWindow>(emptyAuditLogCustomWindow);
    const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
    const [areFiltersOpen, setAreFiltersOpen] = useState(false);
    const [isRailCollapsed, setIsRailCollapsed] = useState(false);
    const [isExportingCsv, setIsExportingCsv] = useState(false);
    const tablePreferences = useAuditLogTablePreferences();
    const { setSnackbarProps } = useSnackbar();

    const parsed = useMemo(() => parseAuditLogQuery(appliedQuery), [appliedQuery]);
    const auditWindow = useAuditLogWindow(period, customWindow);
    const filters = useMemo(
        () => ({ ...parsed.filters, from: auditWindow.from, to: auditWindow.to }),
        [parsed.filters, auditWindow],
    );

    const { entries, facets, isLoading, isFetching, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
        useGetAuditLogFeed(filters);

    const sections = useAuditLogFacetSections({ facets, filters: parsed.filters });
    const activeFacetCount = sections.filter((section) => !constrainsNothing(section.selection)).length;

    const applyQuery = (nextFilters: AuditLogQueryFilters) => {
        setAppliedQuery(buildAuditLogQuery(nextFilters));
    };

    const changeSection = (section: AuditLogFacetSection, selection: FacetSelection) => {
        applyQuery(section.select(selection) as AuditLogQueryFilters);
    };

    const focusFilters = (nextFilters: AuditLogQueryFilters) => {
        applyQuery({ ...parsed.filters, ...nextFilters });
        setSelectedEntry(null);
    };

    const focusObjectHistory = (objectFilters: AuditLogQueryFilters) => {
        applyQuery(objectFilters);
        setPeriod("all");
        setSelectedEntry(null);
    };

    const resetFacets = () => {
        applyQuery({
            q: parsed.filters.q,
            entityId: parsed.filters.entityId,
            domain: parsed.filters.domain,
            rootId: parsed.filters.rootId,
        });
    };

    const exportCsv = async () => {
        setIsExportingCsv(true);

        try {
            const exportedEntries = await fetchAllAuditLogEntries(filters);
            const blob = new Blob([auditLogToCsv(exportedEntries)], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = auditLogCsvFilename(new Date(), exportedEntries.length);
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            setSnackbarProps({
                message: t("settings.audit_log.export_error", "The export failed. Try again."),
                type: "error",
            });
        } finally {
            setIsExportingCsv(false);
        }
    };

    const hasEntries = entries.length > 0;
    const total = auditLogDisplayTotal(auditWindow, facets?.perDay);

    return (
        <WhiteBox margin={isMobile ? 0 : 3}>
            <Stack padding={theme.spacing(3)} gap={theme.spacing(2)}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={theme.spacing(2)}
                    flexWrap="wrap"
                >
                    <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                        <FoodsIconButton
                            icon="arrowLeftShort"
                            size="s"
                            tooltip={{ text: t("settings.audit_log.back", "Back to settings") }}
                            buttonProps={{
                                onClick: () => navigate(URLHandler.settings.configurationPath("access-permissions")),
                            }}
                        />
                        <Typography variant="h3">{t("settings.audit_log.title", "Audit log")}</Typography>
                    </Stack>
                    <Stack direction="row" gap={theme.spacing(1)} alignItems="center">
                        <Button
                            variant="secondary"
                            startIcon={<FoodsIcon icon="exportDownload" size={2} />}
                            onClick={() => void exportCsv()}
                            disabled={isExportingCsv || hasEntries === false}
                        >
                            {isExportingCsv ?
                                t("settings.audit_log.exporting", "Exporting…")
                            :   t("settings.audit_log.export", "Export CSV")}
                        </Button>
                        {!isMobile && <AuditLogTableMenu {...tablePreferences} />}
                    </Stack>
                </Stack>

                <AuditLogQueryBar
                    tokens={tokenizeAuditLogQuery(appliedQuery)}
                    onTokensChange={(tokens) => setAppliedQuery(tokens.join(" "))}
                    facets={facets}
                    period={period}
                    onPeriodChange={setPeriod}
                    parsed={parsed}
                    isCompact={isMobile}
                    appliedWindow={customWindow}
                    customWindow={customWindowDraft}
                    onCustomWindowChange={setCustomWindowDraft}
                    onApplyCustomWindow={() => setCustomWindow(customWindowDraft)}
                    trailing={
                        isMobile ?
                            <Button
                                variant="secondary"
                                startIcon={<FoodsIcon icon="filter" size={2} />}
                                onClick={() => setAreFiltersOpen(true)}
                                sx={{ flexShrink: 0 }}
                            >
                                {activeFacetCount === 0 ?
                                    t("settings.audit_log.filters", "Filters")
                                :   t("settings.audit_log.filters_count", "Filters ({{n}})", { n: activeFacetCount })}
                            </Button>
                        :   undefined
                    }
                />

                <AuditLogSummary total={total} displayed={entries.length} from={auditWindow.from} to={auditWindow.to} />

                {isLoading ?
                    <AuditLogLoadingState />
                : isError ?
                    <AuditLogErrorState onRetry={() => void refetch()} />
                : hasEntries === false ?
                    <AuditLogEmptyState
                        hasFacets={activeFacetCount > 0}
                        canWidenWindow={period !== "all" && period !== "custom"}
                        onWidenWindow={() => setPeriod(WIDER_PERIOD[period])}
                        onResetFacets={resetFacets}
                    />
                :   <Stack direction="row" gap={theme.spacing(3)} alignItems="flex-start">
                        {!isMobile && (
                            <AuditLogFacetRail
                                sections={sections}
                                onSectionChange={changeSection}
                                isCollapsed={isRailCollapsed}
                                onCollapsedChange={setIsRailCollapsed}
                            />
                        )}
                        {isMobile ?
                            <AuditLogCardList entries={entries} onEntryClick={setSelectedEntry} />
                        :   <AuditLogGrid
                                entries={entries}
                                isFetching={isFetching}
                                isFetchingNextPage={isFetchingNextPage}
                                onRowClick={setSelectedEntry}
                                selectedEntryId={selectedEntry?.id ?? null}
                                columnVisibility={tablePreferences.columnVisibility}
                                density={tablePreferences.density}
                            />
                        }
                    </Stack>
                }

                {hasNextPage === true && hasEntries && (
                    <Stack alignItems="center">
                        <Button variant="secondary" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                            {isFetchingNextPage ?
                                t("settings.audit_log.loading_more", "Loading…")
                            :   t("settings.audit_log.load_more", "Load older events")}
                        </Button>
                    </Stack>
                )}
            </Stack>

            <AuditLogDrawer
                entry={selectedEntry}
                onClose={() => setSelectedEntry(null)}
                isMobile={isMobile}
                onFocusFilters={focusFilters}
                onViewObjectHistory={focusObjectHistory}
            />

            <AuditLogFilterSheet
                isOpen={areFiltersOpen}
                onOpenChange={setAreFiltersOpen}
                sections={sections}
                onSectionChange={changeSection}
                activeCount={activeFacetCount}
                onReset={resetFacets}
            />
        </WhiteBox>
    );
};
